import { Alert } from '@/components/ui/alert';
import { useGenerateBrandDnaAction } from '@/hooks/actions/use-generate-brand-dna-action';
import loadingAnimation from './assets/loading-animation.json';
import { Box, Button, Flex, Heading, Input, Text, VStack } from '@chakra-ui/react';
import { Link as LinkIcon } from 'iconoir-react';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BrandDnaScanStepProps {
  defaultUrl?: string;
  onGenerated: (text: string, sourceUrl: string) => void;
  onWriteInstead: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
}

const STEP_KEYS = [
  'settings.brandDna.scan.stepAnalyzing',
  'settings.brandDna.scan.stepReading',
  'settings.brandDna.scan.stepStudying',
  'settings.brandDna.scan.stepGenerating',
] as const;

const STEP_INTERVAL = 5000;

const Lottie = lazy(() => import('lottie-react'));

const MotionBox = motion.create(Box);

const BrandDnaScanStep = ({
  defaultUrl = '',
  onGenerated,
  onWriteInstead,
  onLoadingChange,
}: BrandDnaScanStepProps) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(defaultUrl);
  const [isFocused, setIsFocused] = useState(false);
  const generateBrandDna = useGenerateBrandDnaAction();
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    onLoadingChange?.(generateBrandDna.isPending);
  }, [generateBrandDna.isPending, onLoadingChange]);

  useEffect(() => {
    if (!generateBrandDna.isPending) {
      setStepIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setStepIndex((prev) => (prev < STEP_KEYS.length - 1 ? prev + 1 : prev));
    }, STEP_INTERVAL);

    return () => clearInterval(interval);
  }, [generateBrandDna.isPending]);

  const handleAnalyze = () => {
    if (!url.trim()) return;
    generateBrandDna.mutate(
      { websiteUrl: url.trim() },
      {
        onSuccess: (response) => {
          onGenerated(response.data.dna, url.trim());
        },
      }
    );
  };

  const hasError = generateBrandDna.isError;

  if (generateBrandDna.isPending) {
    return (
      <MotionBox
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <VStack gap={6} py={16} align="center">
          {/* Title */}
          <Box textAlign="center">
            <Heading size="xl" fontStyle="italic" fontWeight="semibold" lineHeight="1.3" mb={3}>
              {t('settings.brandDna.scan.generatingTitle')}
            </Heading>
            <Text fontSize="md" color="fg.subtle" maxW="420px" mx="auto" lineHeight="tall">
              {t('settings.brandDna.scan.generatingDescription')}
            </Text>
          </Box>

          {/* Lottie loading animation */}
          <Box w="48px" h="48px">
            <Suspense fallback={<Box w="100%" h="100%" />}>
              <Lottie
                animationData={loadingAnimation}
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            </Suspense>
          </Box>

          {/* Animated step text */}
          <Box h="20px">
            <AnimatePresence mode="wait">
              <MotionBox
                key={stepIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
                  {t(STEP_KEYS[stepIndex])}
                </Text>
              </MotionBox>
            </AnimatePresence>
          </Box>

          {/* URL pill */}
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <Flex
              align="center"
              gap={2}
              bg="gray.50"
              borderRadius="2xl"
              px={4}
              py={2.5}
              border="1px solid"
              borderColor="gray.200"
            >
              <Box color="fg.subtle">
                <LinkIcon width={16} height={16} />
              </Box>
              <Text fontSize="sm" color="fg.subtle" fontWeight="medium">
                {url}
              </Text>
            </Flex>
          </MotionBox>
        </VStack>
      </MotionBox>
    );
  }

  return (
    <VStack gap={5} align="stretch">
      <Text fontWeight="medium">{t('settings.brandDna.scan.title')}</Text>

      {hasError && (
        <Alert status="error" title={t('settings.brandDna.scan.errorTitle')}>
          {t('settings.brandDna.scan.errorGeneric')}
        </Alert>
      )}

      <Box>
        <Input
          value={isFocused ? url : url || defaultUrl}
          onChange={(e) => setUrl(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            setUrl('');
          }}
          onBlur={() => {
            setIsFocused(false);
            if (!url.trim()) setUrl(defaultUrl);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAnalyze();
          }}
          placeholder={t('settings.brandDna.scan.urlPlaceholder')}
          type="url"
        />
      </Box>

      <Flex gap={2} justify="flex-end">
        <Button variant="ghost" onClick={onWriteInstead}>
          {t('settings.brandDna.scan.writeInstead')}
        </Button>
        {hasError && (
          <Button variant="outline" onClick={handleAnalyze}>
            {t('settings.brandDna.scan.tryAgain')}
          </Button>
        )}
        {!hasError && (
          <Button onClick={handleAnalyze} disabled={!url.trim()}>
            {t('settings.brandDna.scan.analyzeButton')}
          </Button>
        )}
      </Flex>
    </VStack>
  );
};

export default BrandDnaScanStep;
